import React, { useState, useEffect } from "react";
import { Tag, Space, message, Switch, Tooltip, Pagination } from "antd";
import Button from "../../../components/common/button";
import Table from "../../../components/common/table";
import View from "../../../assets/icons/view.svg";
import Trash from "../../../assets/icons/trash.svg";
import {
  getCartDiscountRules,
  deleteCartDiscountRule,
  toggleCartDiscountRuleStatus,
} from "../../../api/cartDiscountRules";
import colors from "../../../share/constants/constants";
import DeleteMemberModal from "../../../components/common/modalMember";
import CartDiscountRuleForm from "./form";
import dayjs from "dayjs";

const CartDiscountRulesManager = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchRules();
  }, [currentPage, pageSize]);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const response = await getCartDiscountRules({
        pageNumber: currentPage,
        itemPerPage: pageSize,
      });
      if (response) {
        setRules(response.items || response || []);
        setTotalItems(response.totalItems || response.length || 0);
      }
    } catch (error) {
      console.error("Error fetching rules:", error);
      message.error("Failed to fetch rules");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRule(record);
    setModalVisible(true);
  };

  const handleAddNew = () => {
    setEditingRule(null);
    setModalVisible(true);
  };

  const handleModalSuccess = () => {
    fetchRules();
    setModalVisible(false);
    setEditingRule(null);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingRule(null);
  };

  const showDeleteModal = (id) => {
    setSelectedRuleId(id);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteCartDiscountRule(selectedRuleId);
      message.success("Rule deleted successfully");
      setIsDeleteModalOpen(false);
      setSelectedRuleId(null);
      fetchRules();
    } catch (error) {
      console.error("Error deleting rule:", error);
      message.error("Failed to delete rule");
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await toggleCartDiscountRuleStatus(id, !currentStatus);
      message.success(`Rule ${!currentStatus ? "enabled" : "disabled"} successfully`);
      fetchRules();
    } catch (error) {
      console.error("Error toggling status:", error);
      message.error("Failed to update rule status");
    }
  };

  const getRuleStatus = (rule) => {
    if (!rule.isEnabled) {
      return { text: "Disabled", color: "default" };
    }
    const now = dayjs();
    const startAt = dayjs(rule.startAt);
    const endAt = dayjs(rule.endAt);
    
    if (now.isBefore(startAt)) {
      return { text: "Scheduled", color: "blue" };
    }
    if (now.isAfter(endAt)) {
      return { text: "Expired", color: "red" };
    }
    return { text: "Active", color: "green" };
  };

  const formatSchedule = (rule) => {
    if (!rule.startAt || !rule.endAt) {
      return "Not scheduled";
    }
    const start = dayjs(rule.startAt).format("MMM DD, YYYY HH:mm");
    const end = dayjs(rule.endAt).format("MMM DD, YYYY HH:mm");
    return `${start} - ${end}`;
  };

  const itemRender = (page, type, originalElement) => {
    if (type === "page") {
      return (
        <div
          className={`rounded ${
            currentPage === page
              ? "bg-primaryPurple text-white"
              : "bg-white text-black"
          }`}
          style={{ cursor: "pointer" }}
        >
          {page}
        </div>
      );
    }
    return originalElement;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSizeChange = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const columns = [
    {
      title: "Vendor",
      dataIndex: "vendorId",
      key: "vendor",
      render: (vendorId, record) => {
        const vendorName = record.vendorId?.businessName || vendorId || "Unknown";
        return <div className="max-w-xs truncate">{vendorName}</div>;
      },
    },
    {
      title: "Rule Type",
      dataIndex: "ruleType",
      key: "ruleType",
      render: (type) => {
        const typeMap = {
          discount: "Deal / Service Discount",
          informational: "Informational Banner",
        };
        return <Tag color={type === "discount" ? "purple" : "cyan"}>{typeMap[type] || type}</Tag>;
      },
    },
    {
      title: "Service/Deal",
      dataIndex: "serviceId",
      key: "service",
      render: (serviceId, record) => {
        if (!serviceId) {
          return <span className="text-gray-400">All Items</span>;
        }
        const serviceName = record.serviceId?.serviceName || record.serviceId?.name || serviceId || "Unknown";
        return <div className="max-w-xs truncate">{serviceName}</div>;
      },
    },
    {
      title: "Min Amount",
      dataIndex: "minimumAmount",
      key: "minimumAmount",
      render: (amount) => `$${parseFloat(amount || 0).toFixed(2)}`,
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, record) => {
        if (record.ruleType === "discount") {
          const value = parseFloat(record.discountValue || 0);
          const type = record.discountType === "percentage" ? "%" : "$";
          return `${value}${type}`;
        }
        return <span className="text-gray-400">N/A</span>;
      },
    },
    {
      title: "Schedule",
      key: "schedule",
      render: (_, record) => {
        return <div className="max-w-xs text-sm">{formatSchedule(record)}</div>;
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        const status = getRuleStatus(record);
        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-2 w-[140px]">
          <Tooltip placement="top" title="Edit">
            <div
              onClick={() => handleEdit(record)}
              className="w-4 m-auto grid justify-center h-4 pt-1 rounded-full bg-lightLavender cursor-pointer"
            >
              <img src={View} alt="Edit" />
            </div>
          </Tooltip>
          <Tooltip placement="top" title={record.isEnabled ? "Disable" : "Enable"}>
            <Switch
              checked={record.isEnabled}
              size="small"
              onChange={() => handleToggleStatus(record._id, record.isEnabled)}
            />
          </Tooltip>
          <Tooltip placement="top" title="Delete">
            <div
              onClick={() => showDeleteModal(record._id)}
              className="flex items-center justify-center w-4 h-4 rounded-full bg-lightCrimson cursor-pointer"
            >
              <img src={Trash} alt="Delete" />
            </div>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="grid gap-2 w-full">
      <div className="flex justify-between">
        <div className="flex pt-3 pl-3">
          <div className="flex items-center">
            <span className="text-lg font-bold text-black ml-1">
              Cart Discount Rules Manager
            </span>
          </div>
        </div>
        <div className="mt-2 mr-2">
          <Button
            title="Create New Rule"
            className="w-15.8 h-3.8 br"
            borderRadius={5}
            onClick={handleAddNew}
            color={colors.primaryPurple}
          />
        </div>
      </div>

      <div>
        <div>
          <div className="min-h-screen flex items-start justify-start pt-0">
            <Table columns={columns} data={rules} loading={loading} />
          </div>
        </div>
      </div>

      {totalItems > 0 && (
        <div className="flex justify-center items-center pagination-container mb-2">
          <Pagination
            current={currentPage}
            total={totalItems}
            pageSize={pageSize}
            onShowSizeChange={handleSizeChange}
            pageSizeOptions={[10, 20, 30, 40]}
            onChange={handlePageChange}
            itemRender={itemRender}
            responsive={true}
            className="custom-pagination"
          />
        </div>
      )}

      <CartDiscountRuleForm
        open={modalVisible}
        onCancel={handleModalCancel}
        onSuccess={handleModalSuccess}
        rule={editingRule}
        isEdit={!!editingRule}
      />

      {isDeleteModalOpen && (
        <DeleteMemberModal
          title="Delete Cart Discount Rule"
          text="Are you sure you want to delete this rule?"
          onCancel={() => {
            setIsDeleteModalOpen(false);
            setSelectedRuleId(null);
          }}
          onOk={handleDelete}
        />
      )}
    </div>
  );
};

export default CartDiscountRulesManager;
